import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, TextField, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeSVG } from 'qrcode.react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

export default function DonationModal({ open, onClose, ngo }) {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pixValue = ngo.pix_qr_code_link || ngo.pix;
  const hasQRCode = Boolean(ngo.pix_qr_code_link);
  const hasAnyPixInfo = Boolean(pixValue);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(pixValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset após 2 segundos
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography 
          variant="h6" 
          component="div" 
          fontWeight={700}
        >
          {ngo.name}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={(e) => {
            e.stopPropagation();
            onClose(e);
          }}
          sx={{
            color: 'grey.500',
            '&:hover': {
              color: 'grey.700',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Sua doação faz a diferença! Com ela, podemos continuar nosso trabalho 
            de impacto social e transformar ainda mais vidas. Qualquer valor é bem-vindo 
            e será utilizado com transparência em nossos projetos.
          </Typography>
          
          {hasAnyPixInfo ? (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Para fazer sua doação, {hasQRCode ? 'copie o código PIX abaixo:' : 'use a chave PIX abaixo:'}
              </Typography>

              <TextField
                fullWidth
                value={pixValue}
                variant="outlined"
                onClick={handleCopyClick}
                InputProps={{
                  readOnly: true,
                  sx: { 
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    cursor: 'pointer'
                  }
                }}
                helperText={copied ? hasQRCode ? "Código copiado!" : "Chave copiada!" : ""}
                sx={{ mb: 1 }}
              />
              
              <Typography variant="caption" color="text.secondary">
                {hasQRCode 
                  ? "Após copiar o código, utilize-o em seu aplicativo bancário para realizar a transferência."
                  : "Utilize esta chave PIX em seu aplicativo bancário para realizar a transferência."}
              </Typography>

              {!isMobile && hasQRCode && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <QRCodeSVG 
                    value={ngo.pix_qr_code_link} 
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography 
              variant="body1" 
              color="error" 
              sx={{ 
                mt: 2, 
                mb: 2, 
                p: 2, 
                backgroundColor: 'rgba(211, 47, 47, 0.1)', 
                borderRadius: 1 
              }}
            >
              Esta organização ainda não cadastrou informações de PIX para doação. 
              Por favor, entre em contato diretamente para obter outras formas de contribuir.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pb: 2 }}>
        <Button 
          onClick={(e) => {
            e.stopPropagation();
            onClose(e);
          }}
          variant="outlined"
          sx={{ 
            color: '#294BB6',
            borderColor: '#294BB6'
          }}
        >
          Cancelar
        </Button>
        {hasAnyPixInfo && (
          <Button 
            onClick={(e) => {
              e.stopPropagation();
              handleCopyClick();
            }}
            variant="contained"
            startIcon={<ContentCopyIcon />}
            sx={{ 
              backgroundColor: '#294BB6',
              '&:hover': {
                backgroundColor: '#294BB6',
                opacity: 0.9
              }
            }}
          >
            {copied 
              ? hasQRCode ? 'Código copiado!' : 'Chave copiada!' 
              : `Copiar ${hasQRCode ? 'código PIX' : 'chave PIX'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}